/** Owns appearance preference (mode + per-mode accents) and keeps document chrome in sync. */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

export type ThemeMode = 'light' | 'dark';
export type ThemeModePreference = 'light' | 'dark' | 'system';
export type AccentId = 'orange' | 'green' | 'blue' | 'violet' | 'rose' | 'cyan';

export const ACCENT_IDS: readonly AccentId[] = [
  'orange',
  'green',
  'blue',
  'violet',
  'rose',
  'cyan',
] as const;

const STORAGE_KEY = 'labibur-appearance';
const LEGACY_STORAGE_KEY = 'labibur-theme';
const LIGHT_THEME_COLOR = '#f7f8fb';
const DARK_THEME_COLOR = '#090c0a';

const DEFAULT_ACCENTS: Record<ThemeMode, AccentId> = {
  light: 'orange',
  dark: 'green',
};

type AppearanceState = {
  readonly mode: ThemeModePreference;
  readonly accents: Readonly<Record<ThemeMode, AccentId>>;
};

type ThemeContextValue = {
  readonly mode: ThemeModePreference;
  readonly resolvedTheme: ThemeMode;
  /** @deprecated Prefer resolvedTheme; kept for existing callers. */
  readonly theme: ThemeMode;
  readonly accent: AccentId;
  readonly accents: Readonly<Record<ThemeMode, AccentId>>;
  readonly setMode: (mode: ThemeModePreference) => void;
  readonly setAccent: (accent: AccentId) => void;
  readonly toggleTheme: () => void;
  readonly setTheme: (theme: ThemeMode) => void;
};

const ThemeContext = createContext<ThemeContextValue | null>(null);

function isThemeMode(value: unknown): value is ThemeMode {
  return value === 'light' || value === 'dark';
}

function isThemeModePreference(value: unknown): value is ThemeModePreference {
  return value === 'light' || value === 'dark' || value === 'system';
}

function isAccentId(value: unknown): value is AccentId {
  return typeof value === 'string' && (ACCENT_IDS as readonly string[]).includes(value);
}

function prefersDarkScheme(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-color-scheme: dark)').matches;
}

export function resolveThemeFromPreference(mode: ThemeModePreference): ThemeMode {
  if (mode === 'system') return prefersDarkScheme() ? 'dark' : 'light';
  return mode;
}

function normalizeAccents(raw: unknown): Record<ThemeMode, AccentId> {
  const accents = { ...DEFAULT_ACCENTS };
  if (!raw || typeof raw !== 'object') return accents;
  const record = raw as Record<string, unknown>;
  if (isAccentId(record.light)) accents.light = record.light;
  if (isAccentId(record.dark)) accents.dark = record.dark;
  return accents;
}

function readStoredAppearance(): AppearanceState {
  const fallback: AppearanceState = {
    mode: 'light',
    accents: { ...DEFAULT_ACCENTS },
  };

  if (typeof window === 'undefined') return fallback;

  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as { mode?: unknown; accents?: unknown };
      return {
        mode: isThemeModePreference(parsed.mode) ? parsed.mode : 'light',
        accents: normalizeAccents(parsed.accents),
      };
    }

    const legacy = window.localStorage.getItem(LEGACY_STORAGE_KEY);
    if (isThemeMode(legacy)) {
      const migrated: AppearanceState = {
        mode: legacy,
        accents: { ...DEFAULT_ACCENTS },
      };
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(migrated));
      window.localStorage.removeItem(LEGACY_STORAGE_KEY);
      return migrated;
    }
  } catch {
    // Private mode or blocked storage should not break first paint.
  }

  return fallback;
}

function applyAppearanceToDocument(theme: ThemeMode, accent: AccentId) {
  const root = document.documentElement;
  root.dataset.theme = theme;
  root.dataset.accent = accent;
  root.style.colorScheme = theme;

  const themeColor = document.querySelector<HTMLMetaElement>('meta[name="theme-color"]');
  themeColor?.setAttribute('content', theme === 'dark' ? DARK_THEME_COLOR : LIGHT_THEME_COLOR);
}

/** Resolves initial appearance for SSR-safe consumers and FOUC scripts. */
export function resolveInitialAppearance(): AppearanceState & {
  resolvedTheme: ThemeMode;
  accent: AccentId;
} {
  const appearance = readStoredAppearance();
  const resolvedTheme = resolveThemeFromPreference(appearance.mode);
  return {
    ...appearance,
    resolvedTheme,
    accent: appearance.accents[resolvedTheme],
  };
}

export function ThemeProvider({ children }: { readonly children: ReactNode }) {
  const [appearance, setAppearance] = useState<AppearanceState>(() => {
    const initial = resolveInitialAppearance();
    applyAppearanceToDocument(initial.resolvedTheme, initial.accent);
    return { mode: initial.mode, accents: initial.accents };
  });
  const [systemTheme, setSystemTheme] = useState<ThemeMode>(() =>
    prefersDarkScheme() ? 'dark' : 'light',
  );

  const resolvedTheme =
    appearance.mode === 'system' ? systemTheme : appearance.mode;
  const accent = appearance.accents[resolvedTheme];

  useEffect(() => {
    applyAppearanceToDocument(resolvedTheme, accent);
    try {
      window.localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ mode: appearance.mode, accents: appearance.accents }),
      );
    } catch {
      // Ignore persistence failures; in-memory appearance still works for the session.
    }
  }, [appearance.accents, appearance.mode, accent, resolvedTheme]);

  useEffect(() => {
    const media = window.matchMedia('(prefers-color-scheme: dark)');
    const syncSystem = () => setSystemTheme(media.matches ? 'dark' : 'light');
    syncSystem();
    media.addEventListener('change', syncSystem);
    return () => media.removeEventListener('change', syncSystem);
  }, []);

  const setMode = useCallback((mode: ThemeModePreference) => {
    setAppearance((current) => ({ ...current, mode }));
  }, []);

  const setAccent = useCallback((nextAccent: AccentId) => {
    setAppearance((current) => {
      const theme = resolveThemeFromPreference(current.mode);
      return {
        ...current,
        accents: { ...current.accents, [theme]: nextAccent },
      };
    });
  }, []);

  const setTheme = useCallback((theme: ThemeMode) => {
    setAppearance((current) => ({ ...current, mode: theme }));
  }, []);

  const toggleTheme = useCallback(() => {
    setAppearance((current) => {
      const resolved = resolveThemeFromPreference(current.mode);
      return { ...current, mode: resolved === 'light' ? 'dark' : 'light' };
    });
  }, []);

  const value = useMemo<ThemeContextValue>(
    () => ({
      mode: appearance.mode,
      resolvedTheme,
      theme: resolvedTheme,
      accent,
      accents: appearance.accents,
      setMode,
      setAccent,
      toggleTheme,
      setTheme,
    }),
    [accent, appearance.accents, appearance.mode, resolvedTheme, setAccent, setMode, setTheme, toggleTheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

// Context and hook share one module so the private value type stays local.
// eslint-disable-next-line react-refresh/only-export-components
export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);
  if (!context) throw new Error('useTheme must be used within ThemeProvider.');
  return context;
}
